import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { KineticLine, FadeUp, Overline } from "../components/motion";
import { IMAGES } from "../constants/site";

const milestones = [
  {
    title: "An immigrant's journey",
    text: "My own journey of adapting, learning, building relationships, and contributing to Canadian society shaped who I am. I understand firsthand how education can open doors, create opportunities, and empower young people.",
  },
  {
    title: "30+ years in York Region",
    text: "I have lived, been educated, worked, and actively contributed here. These experiences have given me a deep understanding of the people, families, and diverse communities that make York Region such a special place.",
  },
  {
    title: "A lifetime of service",
    text: "Through volunteerism, advocacy, and community engagement, I have learned the importance of listening, understanding different perspectives, and working collaboratively toward meaningful, practical solutions.",
  },
  {
    title: "Ready to serve",
    text: "I am committed to advocating for students, supporting educators, listening to families, and strengthening the partnership between schools and our communities.",
  },
];

export default function About() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <main data-testid="about-page" className="pt-32 lg:pt-40">
      <section className="mx-auto max-w-7xl px-6 lg:px-10">
        <Overline>About Winnie</Overline>
        <h1 className="mt-6 font-display text-5xl font-bold leading-[0.95] tracking-tighter text-ink sm:text-6xl lg:text-7xl">
          <KineticLine delay={0.1}>Rooted in this</KineticLine>
          <KineticLine delay={0.22}>
            <span className="text-terracotta">community.</span>
          </KineticLine>
        </h1>
      </section>

      <section data-testid="about-story" className="mx-auto mt-20 grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <FadeUp className="lg:col-span-5">
          <div ref={imgRef} className="relative">
            <div className="absolute -left-4 -top-4 h-full w-full bg-sand" aria-hidden="true" />
            <div className="relative overflow-hidden border border-sand">
              <motion.img
                src={IMAGES.portrait}
                alt="Winnie Ho portrait"
                data-testid="about-portrait"
                style={{ y }}
                className="h-[440px] w-full scale-110 object-cover object-top"
              />
            </div>
            <p className="mt-4 font-serif text-sm italic text-stone2">
              Winnie Ho — neighbour, volunteer, and advocate for York Region families.
            </p>
          </div>
        </FadeUp>

        <div className="lg:col-span-7">
          <FadeUp delay={0.1}>
            <p className="text-lg leading-relaxed text-stone2 md:text-xl">
              As an immigrant, I bring a unique perspective shaped by my own journey. I believe
              every student — regardless of their background, culture, abilities, or
              circumstances — deserves the opportunity, support, and encouragement to reach their
              full potential.
            </p>
          </FadeUp>

          <div className="mt-14 space-y-0">
            {milestones.map((m, i) => (
              <FadeUp key={m.title} delay={0.05 * i}>
                <div data-testid={`about-milestone-${i + 1}`} className="group flex gap-8 border-t border-sand py-8 transition-colors duration-300 hover:bg-white/60">
                  <span className="font-display text-3xl font-bold text-stone-300 transition-colors duration-300 group-hover:text-terracotta">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-display text-xl font-bold tracking-tight text-ink md:text-2xl">
                      {m.title}
                    </h3>
                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-stone2 md:text-base">
                      {m.text}
                    </p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-community" className="mx-auto mt-28 max-w-7xl px-6 pb-28 lg:px-10">
        <div className="grid gap-6 md:grid-cols-2">
          <FadeUp>
            <div className="overflow-hidden border border-sand">
              <img
                src={IMAGES.community}
                alt="Community members engaged in conversation"
                data-testid="about-image-community"
                className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          </FadeUp>
          <FadeUp delay={0.15} className="md:mt-16">
            <div className="overflow-hidden border border-sand">
              <img
                src={IMAGES.yorkRegion}
                alt="A tree-lined neighbourhood street in York Region"
                data-testid="about-image-york-region"
                className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </div>
          </FadeUp>
        </div>
        <FadeUp delay={0.2}>
          <p className="mx-auto mt-16 max-w-3xl text-center font-serif text-2xl italic leading-snug text-stone2 md:text-3xl">
            "I believe my life experience, community involvement, and perspective as an immigrant
            can make a positive contribution to our education system."
          </p>
        </FadeUp>
      </section>
    </main>
  );
}
